<template>
  <section class="relative w-full overflow-hidden bg-white dark:bg-ink-950 transition-colors duration-300">
    
    <div class="absolute top-4 right-4 z-30" v-if="isEditMode">
      <button @click="editHero" class="..."> <i class="fa-solid fa-pen-to-square text-brand-600 dark:text-brand-400"></i>
        <span>Éditer</span>
      </button>
    </div>

    <div class="hidden lg:block absolute inset-y-0 right-0 w-1/2 z-0">
      <div class="h-full relative">
        <div v-if="loading" class="w-full h-full bg-ink-100 dark:bg-ink-800 animate-pulse"></div>

        <img
          v-else-if="resolvedImageUrl"
          :src="resolvedImageUrl"
          alt="Hero Image"
          width="1920" 
          height="1080"
          decoding="async"
          loading="eager" 
          fetchpriority="high"
          class="w-full h-full object-cover"
        />

        <div v-else class="w-full h-full bg-gradient-to-br from-ink-50 to-white dark:from-ink-900 dark:to-ink-950"></div>
        <div class="pointer-events-none absolute inset-0 bg-gradient-to-l from-transparent via-black/10 to-black/25 dark:to-black/50"></div>
      </div>
    </div>

    <div class="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
      
      <div class="block lg:hidden w-full mt-2 mb-8 sm:mb-10">
        <div class="relative w-full h-72 rounded-2xl overflow-hidden shadow-[0_16px_40px_-24px_rgba(0,0,0,.35)] ring-1 ring-ink-200 dark:ring-ink-800">
          <div v-if="loading" class="absolute inset-0 bg-ink-100 dark:bg-ink-800 animate-pulse z-10"></div>

          <img
            v-if="resolvedImageUrl"
            :src="resolvedImageUrl"
            alt="Hero Image Mobile"
            width="800"
            height="600"
            decoding="async"
            loading="eager"
            fetchpriority="high"
            class="absolute inset-0 w-full h-full object-cover"
          />
          <div v-else class="absolute inset-0 bg-gradient-to-br from-ink-50 to-ink-100"></div>

          <div class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent z-0"></div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,640px)_1fr] items-center gap-y-10 lg:min-h-[clamp(600px,72vh,840px)] py-8 sm:py-10 lg:py-20">
        <div class="w-full">
          <div class="flex flex-col justify-center text-center lg:text-left lg:pr-24 xl:pr-32">
            
            <div v-if="loading" class="animate-pulse space-y-4">
               <div class="h-10 sm:h-12 w-4/5 mx-auto lg:mx-0 bg-ink-100 dark:bg-ink-800 rounded-lg"></div>
               <div class="h-5 w-5/6 mx-auto lg:mx-0 bg-ink-100 dark:bg-ink-800 rounded"></div>
               <div class="h-5 w-2/3 mx-auto lg:mx-0 bg-ink-100 dark:bg-ink-800 rounded"></div>
               <div class="mt-8 h-10 w-56 mx-auto lg:mx-0 bg-ink-100 dark:bg-ink-800 rounded-full"></div>
            </div>

            <template v-else>
              <h1 class="font-extrabold leading-tight tracking-tight text-ink-900 dark:text-ink-50" :style="{ fontSize: 'clamp(2.25rem,4.2vw,3.5rem)' }">
                {{ titreAffiche }}
              </h1>

              <p class="mt-6 text-[1.05rem] sm:text-lg leading-relaxed max-w-[56ch] mx-auto lg:mx-0 text-ink-700 dark:text-ink-300">
                {{ descriptionAffiche }}
              </p>

              <div class="mt-10 flex flex-col sm:flex-row justify-center lg:justify-start gap-3 sm:gap-4">
                <template v-for="(bouton, index) in heroButtons" :key="bouton.id || index">
                  <router-link
                    v-if="!isExternal(bouton.action)"
                    :to="localized(bouton.action || '#')"
                    class="relative group inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-full overflow-hidden transition-all duration-300 shadow-[0_2px_10px_rgba(0,0,0,.06)] hover:shadow-[0_6px_18px_rgba(0,0,0,.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                    :class="buttonClass(bouton, index)"
                  >
                    <span class="relative z-10">{{ bouton.label || (getLangue === 'en' ? 'Discover' : 'Découvrir') }}</span>
                    <i class="fas fa-arrow-right text-xs relative z-10 group-hover:translate-x-1 transition-transform"></i>
                    <span class="absolute inset-0 rounded-full bg-black/10 dark:bg-white/10 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] pointer-events-none"></span>
                  </router-link>
                  </template>
              </div>
            </template>
          </div>
        </div>
        <div class="hidden lg:block"></div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";

const API_BASE = String(process.env.VUE_APP_API_BASE || "").replace(/\/+$/, "");
const UPLOADS_ORIGIN = String(process.env.VUE_APP_UPLOADS_ORIGIN || API_BASE).replace(/\/+$/, "");

export default {
  name: "HeroComponent",
  props: {
    isEditMode: { type: Boolean, default: false },
    pageId: { type: [Number, String], default: null },
    pageCode: { type: String, default: "home" },
  },
  data() {
    return {
      heroContent: null,
      heroButtons: [],
      heroImage: null,
      loading: true,
      // Suppression de imageLoaded et preloadImage qui ralentissent le LCP
    };
  },
  computed: {
    ...mapGetters(["getLangue"]),

    titreAffiche() {
      return this.heroContent?.titre || (this.getLangue === "en" ? "Welcome" : "Bienvenue");
    },
    descriptionAffiche() {
      return (
        this.heroContent?.description ||
        (this.getLangue === "en"
          ? "Your ideal partner to make your projects succeed."
          : "Votre partenaire idéal pour réussir vos projets.")
      );
    },
    resolvedImageUrl() {
      const raw = this.heroImage?.image_url || this.heroImage?.url || this.heroImage?.path || "";
      if (!raw) return "";
      if (/^\/uploads\//i.test(raw)) {
        const base = String(UPLOADS_ORIGIN || "").replace(/\/+$/, "");
        return base ? `${base}${raw}` : raw;
      }
      if (/^https?:\/\//i.test(raw)) return raw;
      const base = String(API_BASE || "").replace(/\/+$/, "");
      return raw.startsWith("/") ? `${base}${raw}` : `${base}/${raw}`;
    },
    // Suppression de bgStyle et bgClass car on utilise <img> maintenant
  },
  watch: {
    getLangue(newLang) { this.loadData(newLang); },
  },
  methods: {
    isExternal(href) { return /^https?:\/\//i.test(String(href || "")); },
    ensureLocalePrefix(path, lang) {
      if (!path) return `/${lang}`;
      if (this.isExternal(path)) return path;
      if (/^\/(fr|en)\b/.test(path)) return path.replace(/^\/(fr|en)/, `/${lang}`);
      return path.startsWith("/") ? `/${lang}${path}` : `/${lang}/${path}`;
    },
    localized(path) { return this.ensureLocalePrefix(path, this.getLangue); },

    // Suppression de preloadImage() : le navigateur le fait mieux nativement avec <img loading="eager">

    async resolvePageId(lang) {
      const direct = Number(this.pageId);
      if (Number.isFinite(direct) && direct > 0) return direct;
      const resolved = await fetchPageIdByCode(this.pageCode || "home", lang || "fr");
      const n = Number(resolved?.page_id ?? resolved?.id ?? resolved);
      return Number.isFinite(n) && n > 0 ? n : null;
    },

    async loadData(lang) {
      const l = String(lang || this.getLangue || this.$route.params.langue || "").trim();
      if (!l || l === "undefined" || l === "null") return;

      this.loading = true;
      try {
        const pageId = await this.resolvePageId(l);
        if (!pageId) {
          this.heroContent = null; this.heroButtons = []; this.heroImage = null;
          this.loading = false;
          return;
        }

        const { data } = await http.get("hero", { params: { page_id: pageId } });
        const title = data?.title || null;
        const buttons = Array.isArray(data?.buttons) ? data.buttons : [];
        const images = Array.isArray(data?.images) ? data.images : [];

        this.heroContent = title ? { id: title.id, titre: title.titre || "", description: title.description || "" } : null;
        this.heroButtons = buttons.map((b, idx) => ({
          id: b.id, contenu_id: b.contenu_id,
          label: b.label || (l === "en" ? "Discover" : "Découvrir"),
          action: b.action || "#",
          style: b.style || (idx === 0 ? "primary" : "secondary"),
        }));
        this.heroImage = images.length ? images[0] : null;

      } catch (error) {
        if (error.response && error.response.status !== 400) console.error("[HeroComponent] loadData error:", error);
        this.heroContent = null; this.heroButtons = []; this.heroImage = null;
      } finally {
        this.loading = false;
      }
    },
    editHero() {
      this.$emit("edit", { heroTitle: this.heroContent, heroButtons: this.heroButtons, heroImage: this.heroImage });
    },

    buttonClass(bouton, index) {
      const isPrimary = (bouton.style || (index === 0 ? "primary" : "secondary")) === "primary";
      return isPrimary
        ? "bg-brand-600 text-white hover:bg-brand-700 focus:ring-brand-300 dark:focus:ring-brand-900"
        : "bg-white text-ink-800 border border-ink-200 hover:bg-ink-50 focus:ring-ink-300 dark:bg-ink-900 dark:text-ink-200 dark:border-ink-700 dark:hover:bg-ink-800 dark:hover:text-white";
    },
  },
  mounted() {
    this.loadData(this.getLangue);
  },
};
</script>

<style scoped>
/* Suppression des styles spécifiques à background-image car obsolètes */
@media (prefers-reduced-motion: reduce) {
  .transition, .transition-all, .transition-opacity, .transition-transform { transition: none !important; }
}
</style>